import type { ChannelType, Institution } from '@faceup/utils'
import { createContext, type ReactNode, useContext, useMemo } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'

/**
 * Report-form context only: the ids and flags the page flows read. Layout data
 * lives in InstitutionLayout_reportSource. Do NOT grow this fragment with
 * layout/UI fields; colocate a fragment on the consuming component instead.
 */
const fragments = {
  ReportSourceProvider_reportSource: graphql(`
    fragment ReportSourceProvider_reportSource on PublicReportSource {
      id
      sourceType
      institution {
        id
        type
        isTesting
      }
    }
  `),
}

export type ReportSource = {
  /** the report source global id - the legacy access-key name for it; used as the
   * `reportSourceId` argument (a PublicReportSourceGlobalId) of the page queries */
  defaultFormId: string
  channelType: ChannelType
  institutionId: string
  password: string | null
  type: Institution | null
  isTesting: boolean | null
}

const ReportSourceContext = createContext<ReportSource | null>(null)

type ReportSourceProviderProps = {
  children: ReactNode
  reportSource: FragmentType<typeof fragments.ReportSourceProvider_reportSource>
  password: string | null
}

export const ReportSourceProvider = ({
  children,
  reportSource: _reportSource,
  password,
}: ReportSourceProviderProps) => {
  const reportSource = getFragmentData(fragments.ReportSourceProvider_reportSource, _reportSource)

  const value = useMemo<ReportSource>(
    () => ({
      defaultFormId: reportSource.id,
      channelType: reportSource.sourceType,
      institutionId: reportSource.institution.id,
      password,
      type: reportSource.institution.type ?? null,
      isTesting: reportSource.institution.isTesting ?? null,
    }),
    [reportSource, password]
  )

  return <ReportSourceContext.Provider value={value}>{children}</ReportSourceContext.Provider>
}

/** Returns null only outside a report-source branch. */
export const useReportSource = () => useContext(ReportSourceContext)
